import { Injectable } from "@angular/core";
import { EncrDecrService } from "../utils/encdec.service";

/**
 * Serviço para interpretar o payload do JWT após o login.
 *
 * Tokens novos trazem `user` e `roles` dentro de um campo `enc` (AES, mesma chave dos corpos `pld`).
 * Tokens antigos ainda podem expor `id`, `user` e `roles` em texto claro no payload — ambos são suportados.
 */
@Injectable({
  providedIn: "root",
})
export class JwtPayloadService {
  constructor(private encDec: EncrDecrService) {}

  /**
   * Decodifica o segundo segmento do JWT (base64url) para um objeto JSON, sem descriptografar `enc`.
   *
   * @param token - JWT completo (três segmentos separados por `.`)
   * @returns Objeto do payload ou `null` se o token for inválido
   */
  decodeJwtPayloadRaw(token: string): Record<string, unknown> | null {
    try {
      const base64Url = token.split(".")[1];
      if (!base64Url) {
        return null;
      }
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  /**
   * Retorna o payload do JWT com `user` e `roles` utilizáveis, independentemente do formato.
   *
   * - Se existir `enc`, descriptografa e mescla `user` e `roles` vindos do blob interno.
   * - Caso contrário, usa o payload legado (`user` e `roles` no próprio JSON).
   *
   * @param token - JWT armazenado (ex.: `sessionStorage.getItem('x')`)
   * @returns Objeto com claims relevantes; objeto vazio se falhar
   */
  getMergedPayload(token: string): Record<string, unknown> {
    const raw = this.decodeJwtPayloadRaw(token);
    if (!raw) {
      return {};
    }
    if (raw["enc"] && typeof raw["enc"] === "string") {
      try {
        const inner = this.encDec.get(raw["enc"] as string) as Record<string, unknown>;
        const { enc: _omit, ...rest } = raw;
        return { ...rest, ...inner };
      } catch {
        return {};
      }
    }
    return raw;
  }
}
